<script setup lang="ts">
import { UiAccordionVariant } from '@afs/design-system'
import type UiAccordion from '@afs/design-system/components/molecules/UiAccordion/UiAccordion.vue.d.ts'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { navigate } from 'vike/client/router'
import { useFormatSeoContent } from '@/composables/useFormatSeoContent'
import { useGtmOnPageMounted, useGtmSimulatorClick } from '@/composables/useGtmTag'
import type { ParentHomeData } from '@/pages/(app)/index/+data'
import type { ProductPageData } from '@/pages/(app)/@productHub/@product/+data'
import { usePageContext } from 'vike-vue/usePageContext'
import { useSimulation } from '@/composables/useSimulation'
import { openLegalNotice, scrollToLegalNotice } from '@/composables/useAccordion'

const props = withDefaults(defineProps<Props>(), {
  data: () => <ParentHomeData | ProductPageData>{},
})

type Props = {
  data: ParentHomeData | ProductPageData
  displayBreadcrumb: boolean
}

if (props.data?.product) {
  useFormatSeoContent(props.data.product.seoTitle, props.data.product.hero.subtitle)
  useGtmOnPageMounted('category')
}

const { t } = useI18n()
const trackSubmit = useGtmSimulatorClick()

const pageContext = usePageContext()

const legalNoticeAccordion = ref<InstanceType<typeof UiAccordion> | null>(null)
onMounted(() => openLegalNotice(legalNoticeAccordion.value))

const { heroProps, offerTipsBlocProps, additionnalLegalProps } = useSimulation(props.data?.product)

const breadcrumb = computed(() => {
  return {
    ariaLabel: t('breadcrumb.ariaLabel'),
    items: [
      { text: t('page.accueil'), to: '/' },
      {
        text:
          props.data.product?.hub.title && props.data.product.hub.title.length > 40
            ? `${props.data.product?.hub.title.slice(0, 40)}...`
            : props.data.product?.hub.title,
        to: `/${pageContext.routeParams.productHub}`,
      },
      {
        text:
          props.data.product?.breadcrumbTitle && props.data.product.breadcrumbTitle.length > 40
            ? `${props.data.product.breadcrumbTitle.slice(0, 40)}...`
            : props.data.product?.breadcrumbTitle,
      },
    ],
  }
})

const onSimulatorSubmit = (payload: string | number | null, event?: Event) => {
  if (typeof payload === 'string') return

  if (event && event.type === 'submit') {
    const submitEvent = event as SubmitEvent

    if (submitEvent.submitter?.innerText) {
      trackSubmit(submitEvent.submitter.innerText)
    }
  }

  const searchParams = new URLSearchParams()
  if (payload) searchParams.set('amount', payload?.toString())
  searchParams.set('offer', props.data.product?.offerId ?? '')
  navigate(`/simulateur?${searchParams}`)
}

const productSimulatordProps = props.data.product?.hero.simulator
  ? {
      ...props.data.product.hero.simulator,
      action: onSimulatorSubmit,
    }
  : undefined

const legalNotice = {
  id: 'product-legal-notice',
  title: t('homepage.mentionLegal.title'),
}

// On force la mise à jour du contenu en cas de navigation entre les différentes pages produits
watch(
  () => pageContext.routeParams.product,
  async () => window.location.reload(),
)
</script>

<template>
  <div
    v-if="props.data.product"
    class="Product"
  >
    <molecules-ui-breadcrumb
      v-if="displayBreadcrumb"
      v-bind="breadcrumb"
      class="Product__breadcrumb"
    />
    <organisms-ui-hero
      v-if="heroProps"
      class="Hero"
      v-bind="heroProps"
      :simulator="productSimulatordProps"
      :legal-notice-button-label="$t('product.hero.legalNoticeButton')"
      @hero::on-legal-notice-click="scrollToLegalNotice(legalNoticeAccordion)"
    />
    <div class="container my-8">
      <section
        v-if="props.data.product.subOffer"
        class="grid mb-8"
      >
        <molecules-ui-sub-offer
          class="Product__subOffer"
          v-bind="props.data.product.subOffer"
        />
      </section>
      <section
        v-if="props.data.product.contactBlock"
        class="Product__contact grid mb-8"
      >
        <molecules-ui-rich-text
          v-if="props.data.product.contactBlock.text"
          class="Product__contact__text"
          :content="props.data.product.contactBlock.text"
        />
        <molecules-ui-contact
          v-if="props.data.product.contactBlock.contact"
          class="Product__contact__phone"
          v-bind="props.data.product.contactBlock.contact"
        />
      </section>
      <section class="Product__insurance grid">
        <molecules-ui-focus
          v-bind="props.data.product.insuranceBlock"
          illustration="shield-blue"
        />
        <div
          v-if="props.data.product.advantageBlock"
          class="Product__advantages"
        >
          <h2 class="Product__advantages__title heading-2 color-blue-01">
            {{ props.data.product.advantageBlock.title }}
          </h2>
          <ul class="Product__advantages__list g-3">
            <template
              v-for="(item, index) in props.data.product.advantageBlock.items"
              :key="`Product-advantages-advantage-${index}`"
            >
              <li v-if="item.text">
                <molecules-ui-list-item :text="item.text" />
              </li>
            </template>
          </ul>
        </div>
      </section>
      <section
        v-if="props.data.product.adviceBlock"
        class="grid mt-8"
      >
        <molecules-ui-highlight
          class="Product__highlight"
          v-bind="props.data.product.adviceBlock"
        />
      </section>
      <section
        v-if="props.data.product.offerTipsBlock"
        class="Product__offer grid mt-8"
      >
        <molecules-ui-offer-tips
          v-if="offerTipsBlocProps"
          v-bind="offerTipsBlocProps"
        />
      </section>
    </div>
    <organisms-ui-steps
      v-if="props.data.product.stepsBlock"
      :title="props.data.product.stepsBlock.title"
      :steps="props.data.product.stepsBlock.steps"
      :cta="props.data.product.stepsBlock.cta"
    />
    <div class="container my-8">
      <section class="grid">
        <div class="Product__faq">
          <h2 class="Product__faq__title heading-2 color-blue-01">{{ props.data.product.faqBlock.title }}</h2>
          <molecules-ui-accordion
            :items="props.data.product.faqList"
            :variant="UiAccordionVariant.SECONDARY"
            name="offer-page-faq"
          >
            <template
              v-for="question in props.data.product.faqList"
              :key="question.id"
              #[question.id]
            >
              <h3
                v-if="question.subtitle"
                class="mb-2 heading-4"
              >
                {{ question.subtitle }}
              </h3>
              <molecules-ui-rich-text :content="question.content" />
            </template>
          </molecules-ui-accordion>
        </div>
      </section>
    </div>
    <div class="container my-8">
      <section class="grid">
        <molecules-ui-accordion
          ref="legalNoticeAccordion"
          class="Product__legalNotice mt-4"
          :items="[legalNotice]"
        >
          <template
            v-for="item in [legalNotice]"
            :key="item.id"
            #[item.id]
          >
            <molecules-ui-rich-text
              v-if="additionnalLegalProps"
              class="mt-3"
              :content="additionnalLegalProps"
            />
            <molecules-ui-rich-text
              v-if="props.data.product.legalNoticeBlock.legalNotice"
              class="mt-3"
              :content="props.data.product.legalNoticeBlock.legalNotice"
            />
          </template>
        </molecules-ui-accordion>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'config/spaces';
@use 'utilities/mq';

.Product__breadcrumb {
  position: absolute;
  top: 0;
}

.Product__subOffer,
.Product__highlight,
.Product__faq {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 2 / span 10;
  }
}

.Product__contact,
.Product__insurance {
  align-items: center;
}

.Product__insurance {
  @include mq.media('<medium') {
    row-gap: map.get(spaces.$sizes, '7');
  }

  .Focus {
    grid-column: span 4;

    @include mq.media('>=medium') {
      grid-column: 2 / span 5;
    }
  }
}

.Product__contact__text {
  grid-column: 2 / span 5;
}

.Product__contact__phone {
  grid-column: span 5;
}

.Product__advantages {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: span 5;
  }
}

.Product__advantages__title {
  margin-bottom: map.get(spaces.$sizes, '5');

  @include mq.media('>=medium') {
    margin-bottom: map.get(spaces.$sizes, '6');
  }
}

.Product__advantages__list {
  display: flex;
  flex-direction: column;
}

.Product__offer {
  .OfferTips {
    grid-column: span 4;

    @include mq.media('>=medium') {
      grid-column: 2 / span 10;
    }
  }
}

.Product__faq__title {
  margin-bottom: map.get(spaces.$sizes, '6');
  text-align: center;
}

.Product__rateExample {
  @include mq.media('<medium') {
    row-gap: map.get(spaces.$sizes, '7');
  }
}

.Product__rateExample__left {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 1 / span 6;
  }
}

.Product__rateExample__right {
  grid-column: span 4;
  display: flex;
  flex-direction: column;
  gap: map.get(spaces.$sizes, '5');
  align-items: stretch;

  @include mq.media('>=medium') {
    grid-column: span 6;
    gap: map.get(spaces.$sizes, '6');
  }
}

.Product__rateExample__right__title {
  align-self: center;
}

.Product__legalNotice {
  --scroll-offset: 50px;

  grid-column: span 4;

  @include mq.media('>=medium') {
    --scroll-offset: 200px;

    grid-column: 2 / span 10;
  }

  &:deep(.Accordion__item) {
    scroll-margin: calc(var(--header-height) + var(--scroll-offset));
  }
}
</style>
